import React, { useEffect, useState } from 'react';
import { Form } from 'antd';
import type { FormProps } from 'antd';
import { DocumentType, DocumentPayload } from '@nxsys/types/models/document';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import {
  defaultDocumentTypeOptions,
  defaultDocumentTypeOptionsGroup,
} from '@nxsys/constants/AppConstants';
import { getDocumentTypeOptions } from '@nxsys/utils/rightToWorkOptions';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';

type UpdateModalProps = {
  isOpen: boolean;
  title: string;
  documentSelected: DocumentType | null;
  toggle: () => void;
  onUpdateDocument: (documentId: string, params: DocumentPayload) => void;
  listDisabled?: string[];
};

const UpdateModal: React.FC<UpdateModalProps> = ({
  title,
  isOpen,
  documentSelected,
  toggle,
  onUpdateDocument,
  listDisabled = [],
}: UpdateModalProps) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const [form] = Form.useForm();
  const [isSubmitPending, setIsSubmitPending] = useState(false);

  useEffect(() => {
    if (documentSelected) {
      form.setFieldsValue({
        agencyRef: documentSelected?.agencyRef
          ? {
              ...documentSelected.agencyRef,
              value: documentSelected.agencyRef.id || null,
              label: documentSelected.agencyRef.name,
            }
          : null,
        documentTitle: documentSelected?.documentTitle || null,
        documentType:
          defaultDocumentTypeOptions.find(
            (item) => item.value === documentSelected?.documentType,
          ) || null,
        expiryDate: documentSelected?.expiryDate || null,
        documentDate: documentSelected?.documentDate || null,
        documentFile: documentSelected?.documentFile,
      });
    } else {
      form.resetFields();
    }
  }, [documentSelected]);

  const onCancel = () => {
    console.log('Cancel');
    form.resetFields();
    toggle();
  };

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.log('Success:', values);
    setIsSubmitPending(true);
    if (!documentSelected) return;
    const documentFile = values?.documentFile;
    const params = {
      ...values,
      agencyRef: values?.agencyRef?.value || null,
      documentType: values?.documentType?.value || null,
      expiryDate: values?.expiryDate?.toDate() || null,
      documentDate: values?.documentDate?.toDate() || null,
      documentName:
        documentFile?.name || documentSelected?.documentName || null,
    };
    await onUpdateDocument(documentSelected.id, params);
    setIsSubmitPending(false);
    onCancel();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: 'Cancel',
          variant: 'outlined',
          buttonType: 'primary',
          onClick: onCancel,
        },
        {
          name: 'Save',
          buttonType: 'primary',
          onClick: () => {
            console.log('Create');
            form.submit();
          },
          loading: isSubmitPending,
          disabled: isSubmitPending,
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          label="Agency"
          name="agencyRef"
          rules={[{ required: true, message: 'Please select agency' }]}
          // validateStatus="error"
          // help="Should be combination of numbers & alphabets"
        >
          <AppSelectSync
            type="agency"
            apiType="searchAgency"
            placeholder="Select agency"
            onChangeValue={(value: any) => {
              form.setFieldsValue({ agencyRef: value });
            }}
            disabled={listDisabled.includes('agencyRef')}
          />
        </Form.Item>
        <Form.Item
          label="Document Title"
          name="documentTitle"
          rules={[{ required: true, message: 'Please input document title' }]}
          // validateStatus="error"
          // help="Should be combination of numbers & alphabets"
        >
          <AppInput placeholder="Enter document title" />
        </Form.Item>
        <Form.Item
          label="Document Type"
          name="documentType"
          rules={[{ required: true, message: 'Please input document type' }]}
          // validateStatus="error"
          // help="Should be combination of numbers & alphabets"
        >
          <AppSelect
            options={getDocumentTypeOptions(
              defaultDocumentTypeOptionsGroup,
              payrollCountry,
              documentSelected?.documentType,
            )}
            placeholder="Select document type"
          />
        </Form.Item>
        <Form.Item
          label="Expiry Date"
          name="expiryDate"
          // validateStatus="error"
          // help="Should be combination of numbers & alphabets"
        >
          <AppDatePicker />
        </Form.Item>
        <Form.Item
          label="Document Date"
          name="documentDate"
          // validateStatus="error"
          // help="Should be combination of numbers & alphabets"
        >
          <AppDatePicker />
        </Form.Item>
        <Form.Item
          label="Attachment"
          name="documentFile"
          rules={[{ required: true, message: 'Please select your attchment' }]}
          // validateStatus="error"
          // help="Should be combination of numbers & alphabets"
        >
          <AppUploadFile
            urlFile={documentSelected?.documentFile}
            onChange={(file: File) => {
              form.setFieldsValue({ documentFile: file });
            }}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default UpdateModal;
